import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
// Redux
import { connect } from 'react-redux';
// Redux Form
import {
    Field, reduxForm, formValueSelector, change,
    getFormSyncErrors, submit as submitForm, getFormValues
} from 'redux-form';
import {
    Button,
    FormGroup,
    FormControl,
} from 'react-bootstrap';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
// Stripe - Modern hooks
import { useStripe } from '@stripe/react-stripe-js';
// Components
import Loader from '../../Loader';
// Helpers
import validateStripe from './validateStripe';
import submit from './submit';
import { isEuropeCountry } from '../../../helpers/europeCountryHelpers';
import generateStripePayoutToken from '../../../helpers/generateStripePayoutToken';
// Redux actions
import { startPayoutLoading, stopPayoutLoading } from '../../../actions/Payout/addPayoutAction';
// Locale
import messages from '../../../locale/messages';
import showToaster from '../../../helpers/toasterMessages/showToaster';
import s from '../Payout.css';
import cs from '../../../components/commonStyle.css';

/**
 * Render field component for Redux Form
 * Defined outside component to prevent re-creation on each render
 */
const renderField = ({ input, label, type, meta: { touched, error, dirty }, placeHolder, className, maxLength, formatMessage }) => {
    return (
        <FormGroup className={cx(cs.spaceBottom4, className)}>
            <label>{label}</label>
            <FormControl
                {...input}
                componentClass="input"
                className={cx(cs.formControlInput, 'commonInputPaddingRTL')}
                placeholder={placeHolder}
                maxLength={maxLength}
            />
            {touched && error && <span className={cs.errorMessage}>{formatMessage(error)}</span>}
        </FormGroup>
    );
};

/**
 * Render select field component for Redux Form
 * Defined outside component to prevent re-creation on each render
 */
const renderSelectField = ({ input, label, type, meta: { touched, error, dirty }, children, placeHolder, formatMessage }) => {
    return (
        <FormGroup className={cs.spaceBottom4}>
            <label>{label}</label>
            <FormControl
                {...input}
                componentClass="select"
                className={cs.formControlSelect}
                placeholder={placeHolder}
            >
                {children}
            </FormControl>
            {touched && error && <span className={cs.errorMessage}>{formatMessage(error)}</span>}
        </FormGroup>
    );
};

/**
 * StripePayoutForm - Functional component using modern Stripe hooks
 * 
 * Uses useStripe() hook from @stripe/react-stripe-js instead of legacy injectStripe HOC
 * The stripe.createToken() API remains the same
 */
function StripePayoutForm(props) {
    const {
        handleSubmit, pristine, previousPage, submitting, error,
        siteName, payoutLoading, businessType, payoutCountry,
        formValues, errors, change: changeField,
        startPayoutLoading: startLoading, stopPayoutLoading: stopLoading,
        intl: { formatMessage }
    } = props;

    // Modern Stripe hook - replaces injectStripe HOC
    const stripe = useStripe();

    /**
     * Handle form submission with Stripe token generation
     */
    const handleSubmitAction = async (event) => {
        event.preventDefault();
        let accountToken, personToken;

        // If any error trigger the submit action to show the error messages
        if (errors && JSON.stringify(errors) !== '{}') {
            await handleSubmit();
            return;
        }

        if (!stripe) {
            showToaster({ messageId: 'commonError', toasterType: 'error', requestMessage: 'Stripe no está disponible. Recarga la página.' });
            return;
        }

        if ((!errors || (errors && JSON.stringify(errors) === '{}')) && formValues && stripe) {
            await startLoading();
            const generateTokens = await generateStripePayoutToken(stripe, formValues);
            
            if (generateTokens && generateTokens.status === 200) {
                accountToken = generateTokens.result && generateTokens.result.accountToken;
                personToken = generateTokens.result && generateTokens.result.personToken || null;
                await changeField('isTokenGenerated', true);
                await changeField('accountToken', accountToken);
                await changeField('personToken', personToken);
                await handleSubmit();
            } else {
                showToaster({ messageId: 'commonError', toasterType: 'error', requestMessage: generateTokens.errorMessage });
                await stopLoading();
            }
        }
    };

    return (
        <div className={cx('inputFocusColor', cs.commonBorderSection, 'whiteBgColor')}>
            <form onSubmit={handleSubmit}>
                <h3 className={cx(cs.commonTotalText, cs.paddingBottom4)}>{formatMessage(messages.addPayout)}</h3>
                <Field
                    name="businessType"
                    component={renderSelectField}
                    label={formatMessage(messages.payoutType)}
                    formatMessage={formatMessage}
                >
                    <option value="individual">{formatMessage(messages.payoutIndividual)}</option>
                    <option value="company">{formatMessage(messages.payoutCompany)}</option>
                </Field>
                <div className={cx(s.payoutTitleFlex, s.spaceBetWeen, s.flexColumMobile, s.alignItemBaseLine)}>
                    <Field
                        name="firstname"
                        component={renderField}
                        className={cx([(businessType === 'individual' ? s.cityWidth : s.fullWidth)])}
                        label={(businessType === 'individual' ? formatMessage(messages.payoutFirstName) : formatMessage(messages.payoutCompanyName))}
                        placeHolder={(businessType === 'individual' ? formatMessage(messages.payoutFirstName) : formatMessage(messages.payoutCompanyName))}
                        maxLength={255}
                        formatMessage={formatMessage}
                    />
                    {businessType && businessType === 'individual' && (
                        <Field
                            name="lastname"
                            className={s.cityWidth}
                            component={renderField}
                            label={formatMessage(messages.payoutLastName)}
                            placeHolder={formatMessage(messages.payoutLastName)}
                            maxLength={255}
                            formatMessage={formatMessage}
                        />
                    )}
                </div>
                {payoutCountry && ['US', 'CA', 'GB'].indexOf(payoutCountry) !== -1 && (
                    <Field
                        name="routingNumber"
                        component={renderField}
                        label={payoutCountry === 'GB' ? formatMessage(messages.payoutSortNumber) : formatMessage(messages.payoutRouting)}
                        placeHolder={payoutCountry === 'CA' ? "eg: 11000-000" : (payoutCountry === 'GB' ? "eg: 108800" : "eg: 110000000")}
                        maxLength={50}
                        formatMessage={formatMessage}
                    />
                )}
                <Field
                    name="accountNumber"
                    component={renderField}
                    label={isEuropeCountry(payoutCountry) ? formatMessage(messages.ibanNumber) : formatMessage(messages.accountNumber)}
                    placeHolder={isEuropeCountry(payoutCountry) ? `eg: ${payoutCountry}89370400440532013000` : (payoutCountry === 'GB' ? "eg: 00012345" : "eg: 000123456789")}
                    maxLength={50}
                    formatMessage={formatMessage}
                />
                <Field
                    name="confirmAccountNumber"
                    component={renderField}
                    label={isEuropeCountry(payoutCountry) ? formatMessage(messages.confirmIbanNumber) : formatMessage(messages.confirmAccountNumber)}
                    placeHolder={isEuropeCountry(payoutCountry) ? `eg: ${payoutCountry}89370400440532013000` : (payoutCountry === 'GB' ? "eg: 00012345" : "eg: 000123456789")}
                    maxLength={50}
                    formatMessage={formatMessage}
                />
                {payoutCountry && payoutCountry === 'US' && businessType && businessType === 'individual' && (
                    <Field
                        name="ssn4Digits"
                        component={renderField}
                        label={formatMessage(messages.ssn4Digits)}
                        placeHolder={"1234"}
                        maxLength={4}
                        formatMessage={formatMessage}
                    />
                )}
                <p className={cx(cs.commonMediumText, cs.paddingBottom2)}>{formatMessage(messages.payoutStripeInfo)}</p>
                <p className={cs.commonMediumText}>
                    {formatMessage(messages.stripeTokenInfo1)}{' '}
                    <a href={'https://stripe.com/connect-account/legal'} target={'_blank'} className={cs.siteLinkColor}>{formatMessage(messages.stripeTokenInfo2)}</a>.
                </p>
                <div className={cx(s.btnFlex, cs.spaceTop4)}>
                    <Button className={cx(cs.btnPrimaryBorder, s.btnRight, 'payoutBackRTL')} onClick={previousPage}>
                        <FormattedMessage {...messages.back} />
                    </Button>
                    <Loader
                        type={'button'}
                        buttonType={'button'}
                        className={cs.btnPrimary}
                        disabled={pristine || submitting || error || payoutLoading}
                        show={payoutLoading}
                        label={formatMessage(messages.finish)}
                        handleClick={handleSubmitAction}
                    />
                </div>
            </form>
        </div>
    );
}

StripePayoutForm.propTypes = {
    handleSubmit: PropTypes.any.isRequired,
    previousPage: PropTypes.any.isRequired,
    siteName: PropTypes.string,
    intl: PropTypes.object.isRequired,
};

StripePayoutForm.defaultProps = {
    businessType: 'individual'
};

// Wrap with Redux Form
let StripePayoutFormRedux = reduxForm({
    form: 'PayoutForm',
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
    validate: validateStripe,
    onSubmit: submit
})(StripePayoutForm);

const selector = formValueSelector('PayoutForm');

const mapState = (state) => ({
    siteName: state.siteSettings.data.siteName,
    availableCurrencies: state.currency.availableCurrencies,
    base: state.currency.base,
    payoutLoading: state.reservation.payoutLoading,
    businessType: selector(state, 'businessType'),
    payoutCountry: selector(state, 'country'),
    formValues: getFormValues('PayoutForm')(state),
    errors: getFormSyncErrors('PayoutForm')(state)
});

const mapDispatch = {
    submitForm,
    change,
    startPayoutLoading,
    stopPayoutLoading
};

export default injectIntl(withStyles(s, cs)(connect(mapState, mapDispatch)(StripePayoutFormRedux)));